import React from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import {
  ASSESSMENT_TIMELINE_STEPS,
  TIMELINE_NODE,
  buildAssessmentTimelineNodes,
} from "../utils/assessmentTimeline";

const PURPLE = "#7C3AED";
const GREY = "#D1D5DB";
const LABEL_ACTIVE = "text-violet-700";
const LABEL_PENDING = "text-gray-400";

function TimelineNodeIcon({ state }) {
  if (state === TIMELINE_NODE.COMPLETED) {
    return (
      <span
        className="flex h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: PURPLE }}
        data-testid="timeline-node-completed"
      >
        <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
      </span>
    );
  }

  if (state === TIMELINE_NODE.CURRENT) {
    return (
      <span
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white"
        style={{ border: `3px solid ${PURPLE}` }}
        data-testid="timeline-node-current"
      >
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: PURPLE }}
        />
      </span>
    );
  }

  return (
    <span
      className="flex h-7 w-7 items-center justify-center rounded-full bg-white"
      style={{ border: `2px solid ${GREY}` }}
      data-testid="timeline-node-pending"
    />
  );
}

/**
 * Checkbox-style Assessment Details timeline (non-linear).
 * Line segment after a node is purple only when that node is completed.
 */
const StatusProgressBar = ({ status, vqa = null }) => {
  const nodes = buildAssessmentTimelineNodes({
    status,
    vqa: vqa ?? { vqa_status: status },
  });

  return (
    <div className="w-full overflow-x-auto" data-testid="assessment-timeline">
      <div className="min-w-[720px] px-1">
        <div className="flex items-center">
          {nodes.map((node, index) => (
            <React.Fragment key={node.step}>
              <div className="flex flex-col items-center z-10">
                <TimelineNodeIcon state={node.state} />
              </div>
              {index < nodes.length - 1 ? (
                <div
                  className="h-0.5 flex-1 min-w-[12px]"
                  style={{
                    backgroundColor: node.lineAfterPurple ? PURPLE : GREY,
                  }}
                  data-testid={`timeline-line-${index}`}
                  data-purple={node.lineAfterPurple ? "true" : "false"}
                />
              ) : null}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 hidden custom-breakpoint-10:flex custom-breakpoint-10:justify-between text-sm font-semibold">
          {nodes.map((node) => (
            <div
              key={`label-${node.step}`}
              className={`text-center break-words whitespace-normal max-w-[110px] text-[12px] leading-tight ${
                node.state === TIMELINE_NODE.PENDING
                  ? LABEL_PENDING
                  : LABEL_ACTIVE
              }`}
            >
              {node.step}
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">
        {ASSESSMENT_TIMELINE_STEPS.join(", ")}
      </span>
    </div>
  );
};

export default StatusProgressBar;
